import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    Input,
    computed,
    inject,
    signal,
} from '@angular/core';
import { AvatarModule } from 'primeng/avatar';

import { environment } from '@/environments/environment';

import {
    UserAvatarLike,
    resolveAvatarBg,
    resolveAvatarFg,
    resolveAvatarUrl,
    resolveDisplayName,
    resolveInitials,
} from '../../types/user-profile.types';

export type UserAvatarSize = 'sm' | 'md' | 'lg' | 'xl';

/**
 * Avatar reutilizable para listings, header y página de perfil.
 *
 * - Si hay imagen → la muestra (con onError fallback a iniciales).
 * - Si no hay imagen → iniciales sobre fondo HSL determinístico.
 * - `aria-label` con el nombre para accesibilidad.
 *
 * Diseñado para ser declarativo: pasale el objeto `profile` y listo.
 */
@Component({
    selector: 'app-user-avatar',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, AvatarModule],
    template: `
        @if (imageSrc() && !imageFailed()) {
            <p-avatar
                [image]="imageSrc()!"
                shape="circle"
                [styleClass]="hostClass()"
                [ariaLabel]="ariaLabelComputed()"
                (onImageError)="onImageError()"
            ></p-avatar>
        } @else {
            <p-avatar
                [label]="initials()"
                shape="circle"
                [styleClass]="hostClass()"
                [style]="initialStyle()"
                [ariaLabel]="ariaLabelComputed()"
            ></p-avatar>
        }
    `,
    styles: [
        `
            :host {
                display: inline-flex;
            }

            :host ::ng-deep .app-avatar {
                font-weight: 600;
            }

            :host ::ng-deep .app-avatar-sm {
                width: 32px;
                height: 32px;
                font-size: 0.78rem;
            }

            :host ::ng-deep .app-avatar-md {
                width: 44px;
                height: 44px;
                font-size: 0.95rem;
            }

            :host ::ng-deep .app-avatar-lg {
                width: 72px;
                height: 72px;
                font-size: 1.4rem;
            }

            :host ::ng-deep .app-avatar-xl {
                width: 120px;
                height: 120px;
                font-size: 2.5rem;
            }
        `,
    ],
})
export class UserAvatarComponent {
    @Input() set profile(value: UserAvatarLike | null | undefined) {
        this.profileSig.set(value ?? null);
        this.imageFailed.set(false);
    }

    @Input() set size(value: UserAvatarSize) {
        this.sizeSig.set(value || 'md');
    }

    @Input() ariaLabel?: string;

    private readonly profileSig = signal<UserAvatarLike | null>(null);
    private readonly sizeSig = signal<UserAvatarSize>('md');
    protected readonly imageFailed = signal(false);

    protected readonly imageSrc = computed(() =>
        resolveAvatarUrl(this.profileSig(), environment.api),
    );

    protected readonly initials = computed(() => resolveInitials(this.profileSig()));

    protected readonly hostClass = computed(
        () => `app-avatar app-avatar-${this.sizeSig()}`,
    );

    protected readonly initialStyle = computed(() => ({
        'background-color': resolveAvatarBg(this.profileSig()),
        color: resolveAvatarFg(this.profileSig()),
    }));

    protected readonly ariaLabelComputed = computed(() => {
        if (this.ariaLabel) return this.ariaLabel;
        return resolveDisplayName(this.profileSig(), 'Usuario');
    });

    onImageError(): void {
        this.imageFailed.set(true);
    }
}
